<script setup>
import { router, Head, Link, useForm } from '@inertiajs/vue3';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetValidationErrors from '@/Jetstream/ValidationErrors.vue';
import JetApplicationHeader from '@/Jetstream/ApplicationHeader.vue';
import JetApplicationFooter from '@/Jetstream/ApplicationFooter.vue';
import JetWelcomePanel from '@/Jetstream/WelcomePanel.vue';
import AppTheming from '@/Jetstream/AppTheming.vue';

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const urlNamePrefix = organisation ? "gi." : "";

const form = useForm({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms: false,
    organisation_id: (organisation ? organisation.id : null),
});

const submit = () => {
    form.post(route(urlNamePrefix+'register'), {
        onFinish: () => form.reset('password', 'password_confirmation'),
    });
};

</script>

<template>
    <AppTheming />

    <Head title="Register" />

    <JetApplicationHeader />

    <div id="content-wrapper">

        <div id="main-content" class="container mx-auto flex flex-col md:flex-row justify-between w-full pt-5 pb-5">

            <div id="col-intro" class="md:w-2/3 md:pr-10 lg:pr-40">
                <JetWelcomePanel />
            </div>
            <div id="col-form" class="md:w-1/3">

                <div class="card sos-programme-form-card p-10 rounded-2xl mt-10">

                    <JetValidationErrors class="mb-4" />

                    <div v-if="!organisation && programme.gi" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        {{ $t('dashboard.register.no_organisation') }}
                    </div>

                    <form @submit.prevent="submit" class="" v-else>
                        <div>
                            <JetLabel for="first_name" :value="$t('dashboard.register.first_name')" />
                            <JetInput
                                id="first_name"
                                v-model="form.first_name"
                                type="text"
                                class="mt-1 block w-full"
                                required
                                autofocus
                                autocomplete="first_name"
                            />
                        </div>

                        <div class="mt-4">
                            <JetLabel for="last_name" :value="$t('dashboard.register.last_name')" />
                            <JetInput
                                id="last_name"
                                v-model="form.last_name"
                                type="text"
                                class="mt-1 block w-full"
                                required
                                autofocus
                                autocomplete="last_name"
                            />
                        </div>

                        <div class="mt-4">
                            <JetLabel for="email" :value="$t('dashboard.register.email')" />
                            <JetInput
                                id="email"
                                v-model="form.email"
                                type="email"
                                class="mt-1 block w-full"
                                required
                            />
                        </div>

                        <div class="mt-4">
                            <JetLabel for="password" :value="$t('dashboard.register.password')" />
                            <JetInput
                                id="password"
                                v-model="form.password"
                                type="password"
                                class="mt-1 block w-full"
                                required
                                autocomplete="new-password"
                            />
                        </div>

                        <div class="mt-4">
                            <JetLabel for="password_confirmation" :value="$t('dashboard.register.confirm_password')" />
                            <JetInput
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                type="password"
                                class="mt-1 block w-full"
                                required
                                autocomplete="new-password"
                            />
                        </div>

                        <div class="mt-2">
                            <p class="manage-password text-sm">
                                {{$t('dashboard.register.password_rules')}}
                            </p>
                        </div>

                        <div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="mt-4">
                            <JetLabel for="terms">
                                <div class="flex items-center">
                                    <JetCheckbox id="terms" v-model:checked="form.terms" name="terms" />

                                    <div class="ml-2">
                                        {{$t('dashboard.register.agree')}}<a target="_blank" :href="route(urlNamePrefix+'page', {page:'privacy-policy'})" class="underline text-sm text-gray-600 hover:text-gray-900">{{$t('dashboard.register.privacy_policy')}}</a>
                                    </div>
                                </div>
                            </JetLabel>
                        </div>

                        <div class="flex items-center justify-end mt-4">
                            <Link :href="route(urlNamePrefix+'login')" class="underline text-sm text-gray-600 hover:text-gray-900">
                                {{$t('dashboard.register.registered')}}
                            </Link>

                            <JetButton class="ml-4 uppercase" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                                {{$t('dashboard.register.register')}}
                            </JetButton>
                        </div>
                    </form>

                </div>

            </div>
        </div>
    </div>
    <JetApplicationFooter :programme="programme" :organisation="organisation" />
</template>
